import React, { useContext } from "react";
import {
  IonIcon,
  IonItem,
  IonLabel,
  isPlatform,
  useIonActionSheet,
} from "@ionic/react";
import { ellipsisVertical } from "ionicons/icons";
import { EmployeePhone } from "../models/employees/Employees";
import { useSettingsStore } from "../store/settings";
import { TranslationsContext } from "../util/Translations";
import { filterTextablePhones, sanitizePhoneForUri } from "../util/EmployeeContact";
import useEmployeeContact from "../hooks/useEmployeeContact";

interface EmployeeContactFieldProps {
  label?: string;
  valueText: string;
  employeeId: string;
}

// Open a tel:/sms:/mailto: URI via the OS handler.
const openUri = (uri: string) =>
  window.open(uri, isPlatform("hybrid") ? "_system" : "_blank");

/**
 * A Work Order Details field whose value is an employee. The whole row is a
 * single tap target: tapping it opens an action sheet with Call / Text / Email
 * options (only those the employee actually has). This avoids cramming several
 * small icons together, which is error-prone on touch devices.
 *
 * Text options are limited to numbers whose PhoneCode is configured as
 * SMS-capable in Settings.
 */
const EmployeeContactField: React.FC<EmployeeContactFieldProps> = ({
  label,
  valueText,
  employeeId,
}) => {
  const { translations } = useContext(TranslationsContext);
  const smsCapablePhoneCodes = useSettingsStore(
    (state) => state.smsCapablePhoneCodes
  );
  const [presentActionSheet] = useIonActionSheet();
  const { contact } = useEmployeeContact(employeeId);

  const email = contact?.email;
  const phones = contact?.phones ?? [];
  const textablePhones = filterTextablePhones(phones, smsCapablePhoneCodes);
  const hasContact = phones.length > 0 || !!email;

  const phoneLabel = (p: EmployeePhone) =>
    p.PhoneCode ? `${p.PhoneNumber} (${p.PhoneCode})` : `${p.PhoneNumber}`;

  const openContactSheet = () => {
    const buttons: any[] = [];
    phones.forEach((p) => {
      buttons.push({
        text: `${translations["lbl_call"] || "Call"} ${phoneLabel(p)}`,
        handler: () => {
          openUri(`tel:${sanitizePhoneForUri(p.PhoneNumber)}`);
        },
      });
    });
    textablePhones.forEach((p) => {
      buttons.push({
        text: `${translations["lbl_text"] || "Text"} ${phoneLabel(p)}`,
        handler: () => {
          openUri(`sms:${sanitizePhoneForUri(p.PhoneNumber)}`);
        },
      });
    });
    if (email) {
      buttons.push({
        text: `${translations["lbl_email"] || "Email"} ${email}`,
        handler: () => {
          openUri(`mailto:${email}`);
        },
      });
    }
    buttons.push({
      text: translations["lbl_btn_cancel"] || "Cancel",
      role: "cancel",
    });

    presentActionSheet({
      header: valueText || (translations["lbl_contact"] || "Contact"),
      buttons,
    });
  };

  return (
    <IonItem
      button={hasContact}
      detail={false}
      onClick={hasContact ? openContactSheet : undefined}
    >
      <IonLabel>
        <p>{label}</p>
        <h3>{valueText}</h3>
      </IonLabel>
      {hasContact && (
        <IonIcon
          slot="end"
          icon={ellipsisVertical}
          color="primary"
          aria-label={translations["lbl_contact"] || "Contact"}
        />
      )}
    </IonItem>
  );
};

export default EmployeeContactField;
